import { useSelector } from 'react-redux';

/**
 * OmniConnX v2 brand assets, served from frontend/public.
 * All files come from the OmniConnX_Web_Brand_Package (v2 system: the lens
 * mark replaces the leading O in the wordmark) — used as-is, unmodified.
 */
const BASE = '/OmniConnX_Web_Brand_Package';

export const brandAsset = {
  /** Transparent v2 symbol (lens mark). Available px: 16,32,48,64,96,128,144,152,167,180,192,256,512,1024 */
  symbol: (px = 128) => `${BASE}/icons/omniconnx-v2-symbol-${px}x${px}.png`,
  /** v2 horizontal lockup for LIGHT theme (transparent, dark metallic wordmark). Available w: 400,700,1000,1400,1800,2400 */
  lockupLight: (w = 400) => `${BASE}/web/light/omniconnx-v2-logo-light-${w}w.png`,
  /** v2 horizontal lockup for DARK theme (transparent, silver metallic wordmark). Available w: 400,700,1000,1400,1800,2400 */
  lockupDark: (w = 400) => `${BASE}/web/dark/omniconnx-v2-logo-dark-${w}w.png`,
  /** v2 dark app icon. Available px: 152,167,180,192,256,512,1024 */
  appIcon: (px = 256) => `${BASE}/icons/omniconnx-v2-appicon-dark-${px}x${px}.png`,
  ogImage: `${BASE}/social/omniconnx-v2-og-light-1200x630.png`,
};

const SIZES = {
  sm: { lockup: 'h-8', symbol: 24, text: 'text-base' },
  md: { lockup: 'h-10', symbol: 30, text: 'text-xl' },
  lg: { lockup: 'h-14', symbol: 40, text: 'text-3xl' },
};

/** Plain themed wordmark text — for tight spots (e.g. collapsible sidebar). */
export const BrandWordmark = ({ className = '' }) => (
  <span className={`font-display font-bold text-gray-900 dark:text-white whitespace-nowrap ${className}`}>
    OmniConn
    <span className="bg-gradient-to-br from-orange-400 via-pink-500 to-blue-600 bg-clip-text text-transparent">X</span>
  </span>
);

/**
 * Theme-aware brand logo — official v2 horizontal lockups:
 * light theme -> light lockup (dark lettering), dark theme -> dark lockup
 * (silver lettering). Both transparent. forceSymbol renders symbol + text.
 */
const BrandLogo = ({ size = 'md', withText = true, forceSymbol = false, className = '' }) => {
  const mode = useSelector((state) => state.theme?.mode);
  const s = SIZES[size] || SIZES.md;

  if (withText && !forceSymbol && mode !== 'dark') {
    return (
      <img
        src={brandAsset.lockupLight(400)}
        srcSet={`${brandAsset.lockupLight(400)} 1x, ${brandAsset.lockupLight(700)} 2x`}
        alt="OmniConnX"
        className={`${s.lockup} w-auto ${className}`}
      />
    );
  }

  if (withText && !forceSymbol) {
    // The dark artwork's canvas carries ~40% empty headroom above the
    // bottom-anchored logo (light: 5:1 tight crop, dark: 2.56:1). Render the
    // image ~164% tall inside a fixed-height clip anchored to the bottom so
    // the visible wordmark matches the light lockup at the same nav height —
    // no modification to the asset itself.
    return (
      <span className={`inline-flex items-end overflow-hidden ${s.lockup} ${className}`}>
        <img
          src={brandAsset.lockupDark(400)}
          srcSet={`${brandAsset.lockupDark(400)} 1x, ${brandAsset.lockupDark(700)} 2x`}
          alt="OmniConnX"
          className="h-[164%] w-auto max-w-none"
        />
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <img
        src={brandAsset.symbol(128)}
        alt={withText ? '' : 'OmniConnX'}
        width={s.symbol}
        height={s.symbol}
        className="shrink-0"
      />
      {withText && <BrandWordmark className={s.text} />}
    </span>
  );
};

export default BrandLogo;
